import React from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';

/**
 * Welcome banner component with personalized greeting
 * Features glassmorphism design and quick action buttons
 * Supports theme colors: green, purple, blue
 */
const WelcomeBanner = ({ 
  userName = 'Creator',
  onCreateNew,
  onQuickAction,
  colorTheme = 'green' // 'green', 'purple', 'blue'
}) => {
  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get motivational message
  const getMotivationalMessage = () => {
    const messages = [
      "Ready to create something amazing today?",
      "Your next viral campaign is just a click away.",
      "Let's turn your ideas into stunning ads.",
      "Time to bring your creative vision to life.",
      "Your audience is waiting for great content."
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Theme-aware gradient configurations
  const themeGradients = {
    green: {
      banner: 'from-emerald-600 via-teal-600 to-green-500 dark:from-emerald-700 dark:via-teal-700 dark:to-green-600',
      button: 'text-emerald-600',
      orb: 'bg-emerald-500/20'
    },
    purple: {
      banner: 'from-purple-600 via-pink-600 to-purple-500 dark:from-purple-700 dark:via-pink-700 dark:to-purple-600',
      button: 'text-purple-600',
      orb: 'bg-pink-500/20'
    },
    blue: {
      banner: 'from-blue-600 via-sky-600 to-blue-500 dark:from-blue-700 dark:via-sky-700 dark:to-blue-600',
      button: 'text-blue-600',
      orb: 'bg-sky-500/20'
    }
  };

  const theme = themeGradients[colorTheme] || themeGradients.green;

  return (
    <div 
      className="relative overflow-hidden rounded-3xl mb-8"
      data-testid="welcome-banner"
    >
      {/* Gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${theme.banner}`} />
      
      {/* Animated mesh pattern overlay */}
      <div className="absolute inset-0 opacity-30">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      {/* Floating orbs */}
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
      <div className={`absolute -bottom-20 -left-20 w-48 h-48 ${theme.orb} rounded-full blur-3xl`} />
      
      {/* Content */}
      <div className="relative px-8 py-10 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          {/* Text content */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              <span className="text-white/80 text-sm font-medium">
                Welcome back
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {getGreeting()}, {userName}!
            </h1>
            
            <p className="text-white/70 text-lg max-w-xl">
              {getMotivationalMessage()}
            </p>
          </div>
          
          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onCreateNew}
              className={`
                inline-flex items-center justify-center gap-2
                px-6 py-3 
                bg-white ${theme.button}
                font-semibold rounded-xl
                hover:bg-white/90 hover:scale-105
                transition-all duration-200
                shadow-lg shadow-black/10
              `}
              data-testid="create-new-campaign-btn"
            >
              <Sparkles className="w-5 h-5" />
              Create New Campaign
            </button>
            
            {onQuickAction && (
              <button
                onClick={onQuickAction}
                className="
                  inline-flex items-center justify-center gap-2
                  px-6 py-3
                  bg-white/10 text-white
                  font-medium rounded-xl
                  border border-white/20
                  hover:bg-white/20 hover:scale-105
                  transition-all duration-200
                  backdrop-blur-sm
                "
                data-testid="quick-action-btn"
              >
                Quick Content
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeBanner;
